/**
 * SUPERVISOR.BENCHMARK
 * Update benchmark data
 *
 * No more tabs (2026-09-11) — the "Questions" tab used to host a whole catalog-curation table,
 * big enough to warrant its own tab next to the eNPS account list. Once that table was removed
 * (bank_id replaced its reason to exist, see KB features/bank-questions-vision.md §21-22), all
 * that's left is a small Calculer/Seed/Exporter-audit toolbar — no longer worth a tab switch,
 * shown right above the eNPS list instead.
 *
 * "Dernier calcul le X" + the "Calculer" button live here (2026-09-14, Tom) — same row as the
 * page's own "Benchmark" title, full right — rather than inside SupervisorBenchmarkQuestions'
 * own card, since they trigger a page-level calculation, not something scoped to "Banque de
 * questions" specifically. refreshSignal bumps after a calculate completes so the list re-fetches
 * without this route needing to know anything about its internals.
 *
 * Accounts fetched once here (2026-09-15), passed down as a prop to
 * SupervisorAccountsWithoutSectorWidget rather than fetched there — was previously also fetched
 * independently by a second "Comptes" table widget, doubling the same heavy eNPS-by-account query
 * (getNpsByAccountViaBank, joins fact_notes — 16s in Tom's network tab) on every page load.
 *
 * The sidebar previews accounts awaiting sector assignment. Its directory opens in a modal
 * with sector filters; the directory is hidden while SectorPickerModal is open.
 *
 * managingAccount lives here (uses SectorPickerModal, the same component the template editor's
 * sidebar already uses — "on a un genre de sectorPicker qu'on a fait pour l'éditeur de template")
 * so both the collapsed and expanded views of the reminder card open the same modal instance.
 *
 * sidebarHeight (2026-09-15, "la banque de question prenne la meme hauteur que la liste des
 * comptes") — ResizeObserver on the sidebar column, passed down as SupervisorBenchmarkQuestions'
 * maxHeight so its row list caps/scrolls to match instead of growing taller than the sidebar.
 *
 * Responsive row-count (viewport-derived rowCount, resize listener, 1s poll fallback) tried and
 * reverted the same day — it ended up truncating content on a small screen instead ("c'est
 * devenu tronqué en petit screen reviens comme au début avant que je te demande que ce soit
 * responsive"). Back to flat constants: 8 rows for the accounts reminder card, 8 for "Banque de
 * questions" (Tom's own "plutot 8 par pages stp", from before responsive was ever asked for).
 */

import { useEffect, useRef, useState } from "react"
import { useAppDispatch } from "@/core/root-reducer"
import NavSupervisor from "@/nav/nav.supervisor"
import SupervisorAccountsWithoutSectorWidget from "@/widgets/supervisor-accounts-without-sector.widget"
import SupervisorBenchmarkQuestions from "@/features/benchmark/entities/question/ui/SupervisorBenchmarkQuestions"
import BenchmarkCalcParamsModal from "@/features/benchmark/entities/question/ui/modals/BenchmarkCalcParamsModal"
import SectorPickerModal from "@/features/benchmark/entities/question/ui/modals/SectorPickerModal"
import Button from "@/components/button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalculator, faCircleNotch } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import {
  calculateBenchmarkQuestions,
  calculateBenchmarkGeneral,
  fetchBenchmarkCalculateStatus,
  updateAccountSector_AsSupervisor,
  BenchmarkQuestionCalculateProgress
} from "@/features/benchmark/entities/question/model/benchmarkQuestion.thunk"
import type { CalcQuestionParams } from "@/features/benchmark/entities/question/model/benchmarkQuestion.types"
import { fetchBenchmarkAccounts_AsSupervisor, BenchmarkAccountRow } from "@/features/benchmark/entities/account/model/benchmarkAccount.thunk"

// -1 each — the new "avec/sans benchmark" tabs row above the bank-questions
// list eats into the fixed card height, one row less keeps both cards fitting the page again.
// +3 back (2026-09-17) — isSmall on the accounts widget's rows freed up enough height per row to
// fit more of them again.
const ACCOUNTS_ROW_COUNT = 10
const BANK_PAGE_SIZE = 10 //+1 (2026-09-21) — la place gagnée par le retrait des onglets Toutes/Avec/Sans benchmark

function SupervisorBenchmarkRoute() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [isCalcRunning, setIsCalcRunning] = useState(false)
  //Bonus point from Ben's original feedback batch, deferred at the time ("1/10 + nom de la
  //question" during a bulk calculate) — reuses the same 3s poll already running for
  //isCalcRunning, just reads one more field off the same status payload.
  const [calcProgress, setCalcProgress] = useState<BenchmarkQuestionCalculateProgress | null>(null)
  //Which of the two jobs "Calculer" fires is still running (2026-09-18, Tom) — the questions one
  //usually finishes well before the eNPS one, so once its own progress goes quiet there's still
  //a concrete step to name instead of a generic spinner.
  const [calcStillRunning, setCalcStillRunning] = useState<{ questions: boolean; general: boolean }>({
    questions: false,
    general: false
  })
  const [showCalcModal, setShowCalcModal] = useState(false)
  const [lastQuestionCalculateAt, setLastQuestionCalculateAt] = useState<number | null>(null)
  const [refreshSignal, setRefreshSignal] = useState(0)
  const [accounts, setAccounts] = useState<BenchmarkAccountRow[]>([])
  const [isAccountsLoaded, setIsAccountsLoaded] = useState(false)
  const [hasAccountsError, setHasAccountsError] = useState(false)
  const [managingAccount, setManagingAccount] = useState<BenchmarkAccountRow | null>(null)
  const [sidebarHeight, setSidebarHeight] = useState<number | undefined>(undefined)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatch(fetchBenchmarkCalculateStatus())
      .unwrap()
      .then((res) => setLastQuestionCalculateAt(res.lastQuestionCalculateAt))

    loadAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!sidebarRef.current) return
    const observer = new ResizeObserver((entries) => setSidebarHeight(entries[0].contentRect.height))
    observer.observe(sidebarRef.current)
    return () => observer.disconnect()
  }, [])

  function loadAccounts() {
    setIsAccountsLoaded(false)
    setHasAccountsError(false)
    dispatch(fetchBenchmarkAccounts_AsSupervisor())
      .unwrap()
      .then(setAccounts)
      .catch(() => setHasAccountsError(true))
      .finally(() => setIsAccountsLoaded(true))
  }

  function updateAccountLocal(id: string, key: "sector", value: string | null) {
    setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: value } : a)))
  }

  //Same immediate-select-and-save flow as TemplateBenchmarkSidebar's selectSector — no separate
  //"Enregistrer" step, matching how SectorPickerModal already behaves for the template editor.
  async function saveSector(id: string, sector: string | null) {
    setManagingAccount(null)
    const res = await dispatch(updateAccountSector_AsSupervisor({ accountId: id, sector })).unwrap()
    if (!res.error) updateAccountLocal(id, "sector", sector)
  }

  function handleSelectSector(sector: string) {
    if (managingAccount) saveSector(managingAccount.id, sector)
  }

  function handleResetSector() {
    if (managingAccount) saveSector(managingAccount.id, null)
  }

  function pollUntilDone() {
    if (pollRef.current) clearInterval(pollRef.current)
    setIsCalcRunning(true)
    setCalcProgress(null)
    setCalcStillRunning({ questions: true, general: true })
    pollRef.current = setInterval(async () => {
      try {
        const calcStatus = await dispatch(fetchBenchmarkCalculateStatus()).unwrap()
        if (!calcStatus.calculating) {
          clearInterval(pollRef.current!)
          pollRef.current = null
          setIsCalcRunning(false)
          setCalcProgress(null)
          setLastQuestionCalculateAt(calcStatus.lastQuestionCalculateAt)
          setRefreshSignal((prev) => prev + 1)
        } else {
          setCalcStillRunning({ questions: calcStatus.calculatingQuestions, general: calcStatus.calculatingGeneral })
          // "Calculer" fires the per-question calc (reports progress) and the eNPS one
          // (doesn't) at the same time — calculating stays true off either
          // one. Once the per-question job's own loop finishes, its progress resets to null
          // server-side well before the general job does, so overwriting calcProgress with that
          // null blanked the display back to the generic "chargement" text for most of the wait.
          // Only updating on a real value keeps the last known progress on screen instead.
          if (calcStatus.questionCalculateProgress) setCalcProgress(calcStatus.questionCalculateProgress)
        }
      } catch {
        // réseau instable, on réessaie au prochain tick
      }
    }, 3000)
  }

  //Both fire together (2026-09-14, "on peut faire les 2 en 1") — independent flags/tables
  //server-side, safe concurrently. getCalculateStatus() already ORs both, so pollUntilDone()
  //waits for whichever finishes last without any change needed there.
  async function handleCalculate(params: CalcQuestionParams) {
    await Promise.all([dispatch(calculateBenchmarkQuestions(params)), dispatch(calculateBenchmarkGeneral())])
    pollUntilDone()
  }

  return (
    <NavSupervisor>
      <div className="flex items-center justify-between flex-wrap gap-4" style={{ marginBottom: 20 }}>
        <h1 style={{ marginBottom: 0 }}>Benchmark</h1>
        <div className="flex items-center flex-wrap gap-4" style={{ rowGap: 8 }}>
          {lastQuestionCalculateAt && (
            <span className="grey-t text-sm" style={{ whiteSpace: "nowrap" }}>
              Dernier calcul le {new Date(lastQuestionCalculateAt).toLocaleDateString()}
            </span>
          )}
          {/* isFullWidth lifts .button's 200px max-width (components.css) — without it the
              uppercased label wraps onto two lines. In this flex row the button still sizes
              to its content, it does not stretch across the header. */}
          <Button
            icon={faCalculator}
            isWithBorder
            isFullWidth
            disabled={isCalcRunning}
            onClick={() => setShowCalcModal(true)}>
            {isCalcRunning ? "Calcul en cours…" : "Calculer tous les benchmarks"}
          </Button>
        </div>
      </div>

      {/* position:relative here (not full-page) so the "en cours" overlay below stays confined
          to the cards affected by "Calculer" — a page-wide LoadingModal used to block the whole
          screen including the header, which Tom flagged as heavier than needed (2026-09-15,
          "je veux bien que ça load dans la carte"). */}
      <div style={{ position: "relative" }}>
        {isCalcRunning && (
          <div
            className="flex flex-dcol items-center justify-center"
            style={{ position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.85)", zIndex: 10 }}>
            <FontAwesomeIcon icon={faCircleNotch} spin style={{ fontSize: 32 }} />
            <div className="grey-t text-sm" style={{ marginTop: 8 }}>
              {calcProgress
                ? `${calcProgress.current}/${calcProgress.total}${calcProgress.label ? " — " + calcProgress.label : ""}`
                : !calcStillRunning.questions && calcStillRunning.general
                  ? t("benchmark_calc_step_general_running")
                  : !calcStillRunning.general && calcStillRunning.questions
                    ? t("benchmark_calc_step_questions_running")
                    : t("utils_loading")}
            </div>
          </div>
        )}

        <div className="flex items-start gap-4">
          <div ref={sidebarRef} style={{ width: 320, minWidth: 320, flexShrink: 0 }}>
            <SupervisorAccountsWithoutSectorWidget
              accounts={accounts}
              isLoaded={isAccountsLoaded}
              hasError={hasAccountsError}
              onRetry={loadAccounts}
              onOpenAccount={setManagingAccount}
              isAccountPickerOpen={managingAccount !== null}
              maxShown={ACCOUNTS_ROW_COUNT}
              onAccountsPurged={(result) => {
                setAccounts((prev) => prev.filter(account => !result.deletedIds.includes(account.id)))
                if (result.benchmarksInvalidated) setLastQuestionCalculateAt(null)
                setRefreshSignal((prev) => prev + 1)
              }}
            />
          </div>
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <SupervisorBenchmarkQuestions
              refreshSignal={refreshSignal}
              maxHeight={sidebarHeight}
              pageSize={BANK_PAGE_SIZE}
            />
          </div>
        </div>
      </div>

      {showCalcModal && (
        <BenchmarkCalcParamsModal onClose={() => setShowCalcModal(false)} onConfirm={(params) => handleCalculate(params)} />
      )}

      {managingAccount && (
        <SectorPickerModal
          title={managingAccount.account}
          current={managingAccount.sector}
          onClose={() => setManagingAccount(null)}
          onSelect={handleSelectSector}
          onReset={handleResetSector}
        />
      )}
    </NavSupervisor>
  )
}

export default SupervisorBenchmarkRoute
